import { motion } from 'framer-motion';
import { 
  Car, 
  Gauge, 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  Activity,
  Zap,
  Target
} from 'lucide-react';
import { ProcessingStats } from '../types';
import { formatDuration } from '../utils/helpers';

interface StatsCardsProps {
  stats: ProcessingStats | null;
  isLoading?: boolean;
}

export function StatsCards({ stats, isLoading = false }: StatsCardsProps) {
  const cards = [
    {
      icon: Car,
      label: 'Total Vehicles',
      value: stats?.total_vehicles ?? '-',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-500/10',
      textColor: 'text-blue-400'
    },
    {
      icon: Gauge,
      label: 'Average Speed',
      value: stats ? `${stats.avg_speed.toFixed(1)} km/h` : '-',
      color: 'from-primary-500 to-accent-500',
      bgColor: 'bg-primary-500/10',
      textColor: 'text-primary-400'
    },
    {
      icon: TrendingUp,
      label: 'Maximum Speed',
      value: stats ? `${stats.max_speed.toFixed(1)} km/h` : '-',
      color: 'from-red-500 to-orange-500',
      bgColor: 'bg-red-500/10',
      textColor: 'text-red-400'
    },
    {
      icon: TrendingDown,
      label: 'Minimum Speed',
      value: stats ? `${stats.min_speed.toFixed(1)} km/h` : '-',
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-500/10',
      textColor: 'text-green-400'
    },
    {
      icon: Target,
      label: 'Total Detections',
      value: stats?.total_detections ?? '-',
      color: 'from-amber-500 to-yellow-500',
      bgColor: 'bg-amber-500/10',
      textColor: 'text-amber-400'
    },
    {
      icon: Clock,
      label: 'Processing Time',
      value: stats ? formatDuration(stats.processing_time) : '-',
      color: 'from-cyan-500 to-teal-500',
      bgColor: 'bg-cyan-500/10',
      textColor: 'text-cyan-400'
    }
  ];

  if (stats?.fps) {
    cards.push({
      icon: Zap,
      label: 'Video FPS',
      value: `${stats.fps.toFixed(0)} fps`,
      color: 'from-violet-500 to-purple-500',
      bgColor: 'bg-violet-500/10',
      textColor: 'text-violet-400'
    });
  }

  if (stats?.frame_count) {
    cards.push({
      icon: Activity,
      label: 'Frames Analyzed',
      value: stats.frame_count.toLocaleString(),
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-500/10',
      textColor: 'text-pink-400'
    });
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { duration: 0.4, ease: 'easeOut' }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
    >
      {cards.map((card, index) => {
        const Icon = card.icon;
        
        return (
          <motion.div
            key={index}
            variants={cardVariants}
            whileHover={{ scale: 1.02, y: -2 }}
            className={`
              relative overflow-hidden rounded-xl p-5
              glass border border-dark-700/50
              group cursor-default
            `}
          >
            {/* Background gradient on hover */}
            <div 
              className={`
                absolute inset-0 opacity-0 group-hover:opacity-100 
                bg-gradient-to-br ${card.color} 
                transition-opacity duration-300
              `}
              style={{ opacity: 0.05 }}
            />

            {/* Content */}
            <div className="relative">
              {/* Icon */}
              <div className={`p-2 rounded-lg ${card.bgColor} w-fit mb-3`}>
                <Icon className={`w-5 h-5 ${card.textColor}`} />
              </div>

              {/* Value */}
              <div className="mb-1">
                {isLoading ? (
                  <div className="h-8 w-20 bg-dark-700 rounded animate-pulse" />
                ) : (
                  <motion.p
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-2xl font-bold text-dark-100 font-mono"
                  >
                    {card.value}
                  </motion.p>
                )}
              </div>

              {/* Label */}
              <p className="text-sm text-dark-400">{card.label}</p>
            </div>

            {/* Decorative corner gradient */}
            <div 
              className={`
                absolute -top-10 -right-10 w-24 h-24 
                bg-gradient-to-br ${card.color} 
                rounded-full opacity-10 blur-2xl
              `}
            />
          </motion.div>
        );
      })}
    </motion.div>
  );
}

export default StatsCards;
